import { useEffect, useState } from "react";
import { CornerDownLeftIcon, ListChecksIcon } from "lucide-react";

import { Row } from "@/components/Row";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useCreateAddDashboardTask } from "@/hooks/createAdds/useCreateAddDashboardTask";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

export const QuickAccessModal = () => {
  /* Database CRUD Operations */
  // Read
  const currentUser = useGetCurrentUser();
  // Create
  const { mutate: createTask } = useCreateAddDashboardTask();

  /* React Hooks */
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const showSuccessToast = useSuccessToast();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "t") {
        e.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Ctrl+T",
          MIXPANEL_SCREENS.TASK_TRACKER,
        );
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  /* Handlers */
  const handleClose = () => {
    setIsOpen(false);
    setTitle("");
  };

  const handleCreateTask = () => {
    const trimmed = title.trim();
    if (!trimmed || currentUser == null) return;

    createTask(
      {
        title: trimmed,
        dueDate: dates.getStartOfDay(new Date()),
        assignees: [currentUser.id],
      },
      {
        onSuccess: () => {
          showSuccessToast({ description: "Task created" });
        },
      },
    );
    handleClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateTask();
    }
  };

  /* Render */
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
        else setIsOpen(true);
      }}
    >
      <DialogContent
        className="top-[10%] !translate-y-0 max-w-[40vw] gap-0 overflow-hidden p-0"
        hideCloseButton
      >
        <DialogHeader>
          <DialogTitle className="sr-only">Create a task</DialogTitle>
        </DialogHeader>
        <Row className="items-center gap-2 px-3">
          <ListChecksIcon className="h-4 w-4 shrink-0 opacity-50" />
          <Input
            autoFocus
            className="h-11 border-0 bg-transparent px-0 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Create a task..."
            value={title}
          />
          <Button
            className="shrink-0 opacity-50 hover:opacity-100"
            onClick={handleCreateTask}
            size="icon"
            variant="ghost"
          >
            <CornerDownLeftIcon className="h-4 w-4" />
          </Button>
        </Row>
      </DialogContent>
    </Dialog>
  );
};
